import { useState, useRef, useEffect } from "react";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import { Tooltip, MenuItem } from "@mui/material";
import { Container, Grid } from "@material-ui/core";
import Select from "@material-ui/core/Select";
import CertificationList from "./CertificationList";
import RoundButton from "../../../components/RoundButton";
import _ from 'lodash';
import useToken from "../../../hooks/useToken";
import { useNavigate } from "react-router-dom";
import { routeBuilder } from "../../../utils/routeBuilder";
import Fab from "../../../components/Fab";
import { roles } from "../../../constants/roles";
import Snackbar from "../../../components/Snackbar";
import { commonStrings, certificationStrings } from "../../../components/StringBundle";
import { useGlobalStyles } from "../../../components/globalStyles";
import { statusList } from "./constants";
import { convertToInt, company_name_list, isCompanyRestrictedRole, getCompanyFilterList, getDefaultCompany} from "../../common";
import { useAutoLogout } from "../../../AutoLogoutContext";
import { hideExportExcel } from "../../../utils/utility";

const Certification = () => {
  const navigate = useNavigate();
  const { getCurrentUserDetails } = useToken();
  const currentUserDetails = getCurrentUserDetails();
  const userRole = currentUserDetails?.role;
  const globalClasses = useGlobalStyles();
  const certificationString = certificationStrings.forms;
  const {setCompanyPopupOpen,setSelectedCompanyName,registerSaveHandler} = useAutoLogout();
  const savedStatus = sessionStorage.getItem("certificationStatus");
  const defaultStatus = "CE-ST-1";
  const initialStatus = savedStatus || defaultStatus;
  const [state, setState] = useState(initialStatus);
  const exportExcelRef = useRef();

  const [loadingExcel, setLoadingExcel] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const savedPageNumber = sessionStorage.getItem("selectedpagenumber");
  const initialPageNumber = savedPageNumber ? convertToInt(savedPageNumber) : 1;
  const [activepage, setActivepage] = useState(initialPageNumber);
  const [company, setCompany] = useState(isCompanyRestrictedRole(userRole) ? "CCL" : "All");
  const [showSnack, setSnack] = useState({});
  const [isExportHidden, setIsExportHidden] = useState(false);

  useEffect(() => {
    const hidden = hideExportExcel(userRole, roles);
    setIsExportHidden(hidden);
  }, [userRole]);

  // function to show loading text while exporting excel
  function exportLoadingText() {
    return loadingExcel ? commonStrings.Exporting : commonStrings.ExportToExcel;
  }

  // set loading value in state while exporting excel
  function loadingExcelVal(val) {
    setLoadingExcel(val);
  }

  // on click function to export excel
  const exportToExcel = () => {
    setLoadingExcel(true);
    if (exportExcelRef.current) {
      exportExcelRef.current.exportExcel();
    }
  };

  // handle change function for status dropdown
  const handleChange = (event) => {
    setState(event.target.value); 
    sessionStorage.setItem("certificationStatus", event.target.value);
    setActivepage(1);
  }
 
  // Handler for company change
  const handleCompanyChange = (event) => {
    setCompany(event.target.value);
    sessionStorage.setItem("certificationCompany", event.target.value);
    setActivepage(1);
  };

  // role access for create certification button
  function accessCreateCertification(){
    return ![
      roles.factorymanager,
      roles.executiveLeadership,
      roles.viewOnly,
      roles.readOnly,
      roles.Chairman,
      roles.Auditor,
      roles.ceo,
      roles.BusinessDevelopmentManager,
      roles.BDDomestic,
      roles.managerfinance,
      roles.BDManager,
      roles.BusinessDevelopmentHod,
      roles.marketingExecutive,
      roles.RDManagerBD,
      roles.productionplanning,
      roles.BusinessDevelopmentExecutive
    ].includes(userRole)
  }

  const redirectToCreate = () => {
    navigate(routeBuilder('certification',null, 'create'))
  }

  const goToCertificationsCreate = () => {
    setCompanyPopupOpen(true);
    setSelectedCompanyName(null);
    registerSaveHandler(redirectToCreate);
  }

  return (
    <Container className={globalClasses.sampleroot}>
      {showSnack.open && (
        <Snackbar {...showSnack} handleClose={() => setSnack({ open: false, message: '', severity: '' })} />
      )}
      <Grid container spacing={1} className={globalClasses.margin10}>
        <Grid xs={6} sm={2} md={1} item>
          <FormControl variant="outlined" fullWidth>
            <InputLabel id="company-picklist-label">{commonStrings.company}</InputLabel>
            <Select
              value={company}
              labelId="company-picklist-label"
              onChange={handleCompanyChange}
              label={commonStrings.company}
              id="company-picklist-select"
              disabled={isCompanyRestrictedRole(userRole)}
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null
              }}
            >
              {getCompanyFilterList(userRole).map((item, index) => (
                <MenuItem value={item?.value} key={index}>
                  {item?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={6} sm={3} md={2} item>
          <FormControl variant="outlined" fullWidth>
            <InputLabel htmlFor="outlined-age-native-simple">{commonStrings.pickList}</InputLabel>
            <Select
              value={state}
              onChange={handleChange}
              label={commonStrings.pickList}
              MenuProps={{
                anchorOrigin: { vertical: "bottom", horizontal: "left" },
                transformOrigin: { vertical: "top", horizontal: "left" },
                getContentAnchorEl: null
              }}
            >
              {statusList.map((item, index) => (
                <MenuItem value={item?.value} key={index}>
                  {item?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid xs={12} sm={7} md={9} item
          justifyContent="flex-end"
          alignItems="center"
          className={globalClasses.inlineFlex}
        >
          {isExportHidden && (
            totalRecords === 0 ? (
              <Tooltip title={commonStrings.noDataToExport} placement="top" arrow>
                <span>
                  <Fab
                    onClick={() => exportToExcel()}
                    label={""}
                    disabled
                    iconAddExcel="excel"
                  />
                </span>
              </Tooltip>
            ) : (
              <Tooltip title={commonStrings.exportToExcel} placement="top" arrow>
                <span>
                    <Fab
                      onClick={() => exportToExcel()}
                      label={""}
                      disabled={loadingExcel}
                      iconAddExcel="excel"
                    />
                </span>
              </Tooltip>
            )
          )}
          {accessCreateCertification() &&
            <Fab
              onClick={() => goToCertificationsCreate()}
              label={certificationString.createCertification}
              variant="extended"
            />
          }
        </Grid>
      </Grid>
       <CertificationList
         certificationStatus={state}
         loadingExcelVal={loadingExcelVal}
         activepage={activepage}
         setActivepage={setActivepage}
         company={company}
         exportRef={exportExcelRef}
         totalrecord={totalRecords}
         updateTotalRecords={setTotalRecords}
       />
    </Container>
  );
};

export default Certification;